import React, { useEffect, useState } from 'react';
import { useSubscriptionService } from 'hooks/api/sales/subscriptions';
import { useTenantService } from 'hooks/api/iam/tenant';
import { Board, BoardCard } from 'app/shared/board';
import GmModal from 'app/shared/modal/modal';
import { formatDateForDisplay } from 'app/shared/utils/date';
import { SUBSCRIPTION_COLUMNS } from '../constants';
import {
  computeSubscriptionStatus,
  DAY_MS,
  fetchAllPages,
  mostRecentSubscriptionPerTenant,
  SIX_MONTHS_DAYS
} from '../utils';
import { SubscriptionDetail } from './detail';

export const SubscriptionsBoardPage = () => {
  const { fetchSubscriptions } = useSubscriptionService();
  const { fetchTenants } = useTenantService();

  const [subscriptions, setSubscriptions] = useState([]);
  const [tenants_by_id, setTenantsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected_subscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    handleDataRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDataRequest = async () => {
    try {
      setLoading(true);
      const now = Date.now();
      const six_months_ago = now - SIX_MONTHS_DAYS * DAY_MS;

      const [all_subscriptions, all_tenants] = await Promise.all([
        fetchAllPages(async (page, population) => {
          const { subscriptions, size } = await fetchSubscriptions({
            query_string: `page=${page}&population=${population}&sort_by=-time_stamp&time_stamp=${six_months_ago}~${now}`
          });
          return { items: subscriptions, size };
        }),
        fetchAllPages(async (page, population) => {
          const { tenants, size } = await fetchTenants({
            query_string: `page=${page}&population=${population}`
          });
          return { items: tenants, size };
        })
      ]);

      const valid_tenants = all_tenants.filter(
        (tenant) => tenant.metadata?.is_internal === undefined || !tenant.metadata?.is_internal
      );
      const tenants_by_id = valid_tenants.reduce(
        (acc, tenant) => ({ ...acc, [tenant.id]: tenant }),
        {}
      );
      const valid_subscriptions = all_subscriptions.filter(
        (subscription) => tenants_by_id[subscription.tenant_id]
      );
      setTenantsById(tenants_by_id);
      setSubscriptions(mostRecentSubscriptionPerTenant(valid_subscriptions));
    } catch {
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const items = subscriptions
    .map((subscription) => ({
      ...subscription,
      status: computeSubscriptionStatus(subscription.expires_at)
    }))
    .filter((subscription) => subscription.status);

  const renderCard = (subscription) => {
    const tenant = tenants_by_id[subscription.tenant_id] || {};

    return (
      <BoardCard
        key={subscription.id}
        title={tenant.name || 'Unknown tenant'}
        subtitle={subscription.title || subscription.plan_code}
        meta={formatDateForDisplay(subscription.expires_at)}
        onClick={() => setSelectedSubscription({ ...subscription, tenant })}
      />
    );
  };

  return (
    <>
      <Board
        columns={SUBSCRIPTION_COLUMNS}
        items={items}
        group_by="status"
        loading={loading}
        empty_text="No subscriptions"
        renderCard={renderCard}
      />

      <GmModal
        title="Subscription"
        show_title
        show_modal={!!selected_subscription}
        onClose={() => setSelectedSubscription(null)}
      >
        {selected_subscription && <SubscriptionDetail subscription={selected_subscription} />}
      </GmModal>
    </>
  );
};
